import type { ReactNode } from "react";
import type { DataTableColumn } from "./DataTable";

/**
 * Mobile record-row alternative to `DataTable`, generated from the SAME
 * column/data definition (design.md §6) — never a second, independently
 * maintained field list. Each record renders every column as a labeled
 * field, exposing the same critical data as the desktop table, just in a
 * stacked layout instead of a wide row.
 *
 * `renderFooter` is an optional record-only addition beyond the shared
 * columns (design.md §5.2: "Mobile uses record rows with the same critical
 * data AND direct Monitoreo/Telemetría links" — an addition to, not a
 * replacement of, the shared data columns; the desktop table's single "row
 * action" column stays a plain column instead).
 */
export function RecordList<T>({
  ariaLabel,
  columns,
  rows,
  rowKey,
  recordLabel,
  renderFooter,
  isRowBlocked,
}: {
  ariaLabel: string;
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  recordLabel: (row: T) => string;
  renderFooter?: (row: T) => ReactNode;
  // 07-directrices-movil.md §3: mirrors `DataTable`'s own `isRowBlocked` —
  // the mobile card for a row this app cannot act on yet (e.g.
  // TelemetryEvidenceMatrix's "Conteos no disponibles") stays marked
  // `aria-disabled`, same as its desktop `<tr>`. Optional — every other
  // caller omits it.
  isRowBlocked?: (row: T) => boolean;
}) {
  return (
    <ul aria-label={ariaLabel} className="v2-record-list">
      {rows.map((row) => (
        <li
          key={rowKey(row)}
          aria-label={recordLabel(row)}
          aria-disabled={isRowBlocked?.(row) ? "true" : undefined}
          className="v2-record-list__item"
        >
          {columns.map((column) => (
            <div key={column.key} className="v2-record-list__field">
              <span className="v2-record-list__field-label">{column.header}</span>
              <span className="v2-record-list__field-value">{column.render(row)}</span>
            </div>
          ))}
          {renderFooter && <div className="v2-record-list__footer">{renderFooter(row)}</div>}
        </li>
      ))}
    </ul>
  );
}
